import { useState } from "react";
import {
  CopyButton,
  DataTable,
  ErrorNotice,
  Notice,
  PillGroup,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H3 } from "@/components/tool-kit/styles";
import {
  PH_STATS_STRINGS,
  PhStats,
} from "@/components/tools/chem-reactions/ph-stats";
import {
  type ConstantState,
  initialConstant,
  readConstant,
  WEAK_CONSTANT_STRINGS,
  WeakConstantFields,
} from "@/components/tools/chem-reactions/weak-constant";
import { WORKING_BOX } from "@/components/tools/chemistry/formula-view";
import { t } from "@/lib/i18n/interpolate";
import {
  WEAK_ACIDS,
  WEAK_BASES,
} from "@/lib/tool-kit/chem-reactions/acid-base-data";
import {
  type SoluteSide,
  type WeakSolution,
  weakSolution,
} from "@/lib/tool-kit/chem-reactions/ph";
import {
  fewestSigFigs,
  formatFixed,
  formatSig,
  type NumberFormatter,
} from "@/lib/tool-kit/chemistry/format";
import { parseMeasuredNumber } from "@/lib/tool-kit/math/number-input";

/** English strings for the weak acid and base panel. The pH widget spreads these in. */
export const WEAK_SOLUTION_STRINGS = {
  ...WEAK_CONSTANT_STRINGS,
  ...PH_STATS_STRINGS,
  sideLabel: "The solute is",
  sideAcid: "A weak acid",
  sideBase: "A weak base",
  acidPreset: "Weak acid",
  basePreset: "Weak base",
  acidNames: {
    acetic: "Acetic acid",
    formic: "Formic acid",
    hydrofluoric: "Hydrofluoric acid",
    nitrous: "Nitrous acid",
    hypochlorous: "Hypochlorous acid",
    hydrocyanic: "Hydrocyanic acid",
  },
  baseNames: {
    ammonia: "Ammonia",
    methylamine: "Methylamine",
    dimethylamine: "Dimethylamine",
    trimethylamine: "Trimethylamine",
    aniline: "Aniline",
  },
  concentrationLabel: "Starting concentration (mol/L)",
  badInput:
    "Enter a concentration and a constant that are numbers greater than zero.",
  percentIonised: "Percent ionised",
  methodHeading: "Three ways to solve it",
  columnMethod: "Method",
  columnIon: "Ion concentration x (mol/L)",
  columnPh: "pH",
  methodExact: "Exact charge balance (includes water)",
  methodQuadratic: "Quadratic, water ignored",
  methodApprox: "Approximation x ≈ √(K·C)",
  workingHeading: "The working",
  acidExpression: "Ka = [H⁺][A⁻] ÷ [HA] = x² ÷ (C − x)",
  baseExpression: "Kb = [BH⁺][OH⁻] ÷ [B] = x² ÷ (C − x)",
  quadraticLine:
    "x² + K·x − K·C = 0, so x = (−K + √(K² + 4·K·C)) ÷ 2 = {x} mol/L",
  approxLine: "If x is small, x ≈ √(K·C) = {x} mol/L",
  acidPhLine: "pH = −log₁₀[H⁺] = {ph}",
  basePhLine: "pOH = −log₁₀[OH⁻] = {poh}, so pH = 14.00 − pOH = {ph}",
  exactLine: "Exact value, with water's ions included: pH = {ph}",
  fiveOk:
    "The shortcut value of x is {percent} % of the starting concentration, under 5 %, so x ≈ √(K·C) is valid here.",
  fiveBad:
    "The shortcut value of x is {percent} % of the starting concentration, 5 % or more, so x ≈ √(K·C) is not valid. Use the quadratic or the exact value.",
  copy: "Copy pH",
  copied: "Copied!",
  copyText: "pH = {ph}",
} as const;

type Strings = typeof WEAK_SOLUTION_STRINGS;

const DEFAULT_SIG = 3;
const K_SIG = 2;

function Answer({
  solution,
  side,
  sig,
  strings,
  fmt,
}: {
  solution: WeakSolution;
  side: SoluteSide;
  sig: number;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const guard = sig + 1;
  const ph = (value: number) => formatFixed(fmt, value, sig);
  const { textbook } = solution;
  const percent = formatSig(fmt, textbook.approximateFraction * 100, 2);
  const lines = [
    side === "acid" ? strings.acidExpression : strings.baseExpression,
    t(strings.quadraticLine, { x: formatSig(fmt, textbook.quadratic, guard) }),
    t(strings.approxLine, { x: formatSig(fmt, textbook.approximate, guard) }),
    side === "acid"
      ? t(strings.acidPhLine, { ph: ph(solution.quadraticPh) })
      : t(strings.basePhLine, {
          poh: ph(14 - solution.quadraticPh),
          ph: ph(solution.quadraticPh),
        }),
    t(strings.exactLine, { ph: ph(solution.values.pH) }),
  ];
  return (
    <>
      <PhStats
        decimals={sig}
        fmt={fmt}
        sig={sig}
        strings={strings}
        values={solution.values}
      />
      <StatGrid>
        <StatCard
          label={strings.percentIonised}
          value={`${formatSig(fmt, solution.percentIonised, 2)} %`}
        />
      </StatGrid>
      <div className="space-y-3">
        <h3 className={WIDGET_H3}>{strings.methodHeading}</h3>
        <DataTable
          head={[strings.columnMethod, strings.columnIon, strings.columnPh]}
          rows={[
            [
              strings.methodExact,
              formatSig(fmt, solution.ionExact, guard),
              ph(solution.values.pH),
            ],
            [
              strings.methodQuadratic,
              formatSig(fmt, textbook.quadratic, guard),
              ph(solution.quadraticPh),
            ],
            [
              strings.methodApprox,
              formatSig(fmt, textbook.approximate, guard),
              ph(solution.approximatePh),
            ],
          ]}
        />
        <Notice>
          {t(textbook.fivePercentOk ? strings.fiveOk : strings.fiveBad, {
            percent,
          })}
        </Notice>
      </div>
      <div className="space-y-3">
        <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
        <div className={WORKING_BOX}>
          {lines.map((line) => (
            <p key={line}>{line}</p>
          ))}
        </div>
      </div>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        text={t(strings.copyText, { ph: ph(solution.values.pH) })}
      />
    </>
  );
}

export function WeakSolutionPanel({
  strings,
  fmt,
}: {
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const [side, setSide] = useState<SoluteSide>("acid");
  const [constant, setConstant] = useState<ConstantState>(
    initialConstant(WEAK_ACIDS[0])
  );
  const [concentration, setConcentration] = useState("0.100");

  const constants = side === "acid" ? WEAK_ACIDS : WEAK_BASES;
  const k = readConstant(constant, constants);
  const c = parseMeasuredNumber(concentration);

  function chooseSide(next: SoluteSide) {
    setSide(next);
    setConstant(
      initialConstant((next === "acid" ? WEAK_ACIDS : WEAK_BASES)[0])
    );
  }

  const rawK = constant.preset === "custom" ? constant.text : "";
  const sig = Math.min(
    fewestSigFigs([concentration], DEFAULT_SIG),
    constant.preset === "custom" ? fewestSigFigs([rawK], DEFAULT_SIG) : K_SIG
  );

  return (
    <>
      <PillGroup
        label={strings.sideLabel}
        name="ph-weak-side"
        onChange={chooseSide}
        options={[
          { value: "acid", label: strings.sideAcid },
          { value: "base", label: strings.sideBase },
        ]}
        value={side}
      />
      <WeakConstantFields
        constants={constants}
        idPrefix="ph-weak"
        names={side === "acid" ? strings.acidNames : strings.baseNames}
        onChange={setConstant}
        presetLabel={side === "acid" ? strings.acidPreset : strings.basePreset}
        side={side}
        state={constant}
        strings={strings}
      />
      <TextField
        id="ph-weak-concentration"
        label={strings.concentrationLabel}
        numeric
        onChange={setConcentration}
        value={concentration}
      />
      {k === null || c === null || !(c > 0) ? (
        <ErrorNotice>{strings.badInput}</ErrorNotice>
      ) : (
        <Answer
          fmt={fmt}
          side={side}
          sig={sig}
          solution={weakSolution(side, k, c)}
          strings={strings}
        />
      )}
    </>
  );
}
